import { getRelativeLuminance } from './color-contrast';

const DEFAULT_OTHER_COLOR = '#A0A9B8';

export const VOLUME_POSITIVE_COLOR = '#7FBF85';
export const VOLUME_NEGATIVE_COLOR = '#D98989';

const defaultPlotColors = [
  '#5CA9E9',
  '#70CA97',
  '#E8A18A',
  DEFAULT_OTHER_COLOR, // historical "Other" slot — still used as fallback when no explicit otherColor is set
  '#AB99D2',
  '#D47E93',
  '#95C7F1',
  '#A2DDBB',
  '#F0C2B3',
  '#C1C7D1',
  '#C8BDE2',
  '#E3ABB9',
  '#4A87BA',
  '#5AA179',
  '#BA816E',
  '#808793',
  '#897AA8',
  '#AA6576',
];

export const newThemePlotColors = [
  '#63A2D9',
  '#7FBF85',
  '#E2D68B',
  '#B493BA',
  '#E2AC8B',
  '#6D73B1',
  '#D38C9E',
  '#977B72',
  '#6BB3AE',
  '#B16D98',
  '#B1705C',
  '#7B8E4B',
  '#84848F',
  '#A9CCEA',
  '#AEDEB2',
  '#EAE4BF',
  '#D8C6DC',
  '#E6C3AD',
  '#AFB3D9',
  '#DCB1BC',
  '#CFB7B0',
  '#B2DCD9',
  '#D1A9C2',
  '#D1A294',
  '#C6D2A7',
  '#C8C8CA',
];

const DEFAULT_OTHER_COLOR_DARK = '#808793';

const defaultDarkPlotColors = [
  '#A9CCEA',
  '#7FBF85',
  '#E2D68B',
  '#B493BA',
  '#E2AC8B',
  '#6D73B1',
  '#D38C9E',
  '#977B72',
  '#6BB3AE',
  '#B16D98',
  '#B1705C',
  '#7B8E4B',
  '#84848F',
  '#63A2D9',
  '#AEDEB2',
  '#EAE4BF',
  '#D8C6DC',
  '#E6C3AD',
  '#AFB3D9',
  '#DCB1BC',
  '#CFB7B0',
  '#B2DCD9',
  '#D1A9C2',
  '#D1A294',
  '#C6D2A7',
  '#C8C8CA',
];

/* Figma v1.1 breakdown ramp (pairs strong→light, then grays), plus a small
   tail for deep breakdowns. */
const newBreakdownColors = [
  '#63a2d9',
  '#a9ccea',
  '#7fbf85',
  '#aedeb2',
  '#e2d68b',
  '#eae4bf',
  '#b296b8',
  '#d7c8da',
  '#84848f',
  '#c8c8ca',
  '#4a87ba',
  '#5aa179',
];

const newDarkBreakdownColors = [
  '#a9ccea',
  '#63a2d9',
  '#7fbf85',
  '#aedeb2',
  '#e2d68b',
  '#eae4bf',
  '#b296b8',
  '#d7c8da',
  '#84848f',
  '#c8c8ca',
  '#4a87ba',
  '#5aa179',
];

/**
 * The active chart color palette. Mutated in-place by `setPlotColors`
 * so all existing references see updated values.
 */
export const plotColors: string[] = [...defaultPlotColors];

export const darkPlotColors: string[] = [...defaultDarkPlotColors];

export const breakdownColors: string[] = [...newBreakdownColors];
export const darkBreakdownColors: string[] = [...newDarkBreakdownColors];

/**
 * The active "Other" color, used for generic/residual categories like
 * the "Other" bucket in breakdowns. Kept separate from `plotColors`
 * so it doesn't steal a slot from the ordered palette.
 */
let otherColor: string = DEFAULT_OTHER_COLOR;
let darkOtherColor: string = DEFAULT_OTHER_COLOR_DARK;

const paletteListeners = new Set<() => void>();

export function subscribePaletteChange(listener: () => void): () => void {
  paletteListeners.add(listener);
  return () => {
    paletteListeners.delete(listener);
  };
}

function notifyPaletteChange() {
  for (const listener of paletteListeners) {
    listener();
  }
}

/**
 * Override the chart color palette at runtime (e.g. for whitelabelling).
 * Mutates the existing `plotColors` array in-place so all consumers
 * that import it automatically see the new colors.
 */
export function setPlotColors(colors: string[]) {
  plotColors.length = 0;
  plotColors.push(...colors);
  notifyPaletteChange();
}

/** Reset chart colors back to the Revelio defaults. */
export function resetPlotColors() {
  plotColors.length = 0;
  plotColors.push(...defaultPlotColors);
  notifyPaletteChange();
}

export function setDarkPlotColors(colors: string[]) {
  darkPlotColors.length = 0;
  darkPlotColors.push(...colors);
  notifyPaletteChange();
}

export function resetDarkPlotColors() {
  darkPlotColors.length = 0;
  darkPlotColors.push(...defaultDarkPlotColors);
  notifyPaletteChange();
}

export function setBreakdownColors(colors: string[]) {
  breakdownColors.length = 0;
  breakdownColors.push(...colors);
  notifyPaletteChange();
}

export function resetBreakdownColors() {
  breakdownColors.length = 0;
  breakdownColors.push(...newBreakdownColors);
  notifyPaletteChange();
}

export function setDarkBreakdownColors(colors: string[]) {
  darkBreakdownColors.length = 0;
  darkBreakdownColors.push(...colors);
  notifyPaletteChange();
}

export function resetDarkBreakdownColors() {
  darkBreakdownColors.length = 0;
  darkBreakdownColors.push(...newDarkBreakdownColors);
  notifyPaletteChange();
}

/**
 * Override the "Other" color at runtime (e.g. for whitelabelling).
 * Decouples the generic/residual color from any specific palette index,
 * so ordered categorical bars can use the palette in order.
 */
export function setOtherColor(color: string) {
  otherColor = color;
  notifyPaletteChange();
}

/** Reset the "Other" color back to the Revelio default. */
export function resetOtherColor() {
  otherColor = DEFAULT_OTHER_COLOR;
  notifyPaletteChange();
}

export function setDarkOtherColor(color: string) {
  darkOtherColor = color;
  notifyPaletteChange();
}

export function resetDarkOtherColor() {
  darkOtherColor = DEFAULT_OTHER_COLOR_DARK;
  notifyPaletteChange();
}

export const POSITIVE_COLOR = '#70CA97';
export const NEGATIVE_COLOR = '#D47E93';

export const SENTIMENT_POSITIVE_COLOR = '#7FBF85';
export const SENTIMENT_NEGATIVE_COLOR_LIGHT = '#B35E5E';
export const SENTIMENT_NEGATIVE_COLOR_DARK = '#C98585';

/** Returns the current first color in the active palette (updates with whitelabelling). */
export function getPrimaryColor(): string {
  return plotColors[0] ?? '#5CA9E9';
}

export function getPrimaryColorDark(): string {
  return darkPlotColors[0] ?? '#7DBDF0';
}

/** Returns the current "Other" color (updates with whitelabelling). */
export function getOtherColor(): string {
  return otherColor;
}

/**
 * The dark-mode "Other" grey, set from whitelabel branding in main.tsx
 * (`brand.darkOtherColor`).
 */
export function getOtherColorDark(): string {
  return darkOtherColor;
}

const dimmedPlotColors = ['#F4F4F4', '#E8E8E8'];
const darkDimmedPlotColors = ['#45484C', '#3B3E42'];

const dimmedPlotRamp = ['#F4F4F4', '#E8E8E8', '#DCDCDC', '#D0D0D0'];
const darkDimmedPlotRamp = ['#3B3E42', '#45484C', '#4F5256', '#595C60'];

export const DIMMED_PLOT_RAMP_SIZE = dimmedPlotRamp.length;

const DIMMED_FALLBACK = '#E8E8E8';
const DIMMED_FALLBACK_DARK = '#3B3E42';

export function getDimmedPlotColor(
  color: string,
  theme: 'light' | 'dark',
  dimIndex?: number
): string {
  const dims = theme === 'dark' ? darkDimmedPlotColors : dimmedPlotColors;
  const fallback = theme === 'dark' ? DIMMED_FALLBACK_DARK : DIMMED_FALLBACK;
  if (dimIndex !== undefined && dimIndex >= 0) {
    const ramp = theme === 'dark' ? darkDimmedPlotRamp : dimmedPlotRamp;
    return ramp[dimIndex % ramp.length] ?? fallback;
  }
  const needle = color.toLowerCase();
  const palettes =
    theme === 'dark'
      ? [darkPlotColors, darkBreakdownColors, plotColors, breakdownColors]
      : [plotColors, breakdownColors, darkPlotColors, darkBreakdownColors];
  for (const palette of palettes) {
    const index = palette.findIndex((c) => c.toLowerCase() === needle);
    if (index !== -1) return dims[index % dims.length] ?? fallback;
  }
  return fallback;
}

/**
 * Whether a cell fill is dark enough to need light label text (per-cell
 * luminance, theme-independent). Non-hex fills are treated as light.
 */
export function isDarkFill(fill: string): boolean {
  // Only flip to light text on genuinely dark fills, not mid-tone categoricals.
  return getRelativeLuminance(fill) < 0.24;
}
